import React, { useState } from 'react';
import './VotingPhase.css';
import Button from '../ui/Button';
import { useGame } from '../../contexts/GameContext';
import { submitVote, finalizeVotes } from '../../firebase/services';

const VotingPhase = () => {
  const { lobby, playerId, isHost } = useGame();
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voted, setVoted] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  if (!lobby) {
    return <div className="voting-loading">Yükleniyor...</div>;
  }

  const eliminatedIds = lobby.eliminatedPlayerIds || [];
  const isEliminated = eliminatedIds.includes(playerId);
  const alivePlayers = lobby.players ? lobby.players.filter(p => !eliminatedIds.includes(p.id)) : [];

  const votes = lobby.votes || {};
  const hasVoted = playerId in votes;
  const votedPlayerCount = Object.keys(votes).filter(id => !eliminatedIds.includes(id)).length;
  const totalPlayerCount = alivePlayers.length;
  const roundNumber = (lobby.roundNumber || 0) + 1;
  
  // Submit a vote for a player
  const handleVote = async () => {
    if (!selectedPlayerId || isSubmitting || hasVoted) return;
    
    setIsSubmitting(true);
    try {
      await submitVote(lobby.code, playerId, selectedPlayerId);
      setVoted(true);
    } catch (error) {
      console.error('Vote submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Only the host can finalize votes and move to results
  const handleFinalizeVotes = async () => {
    if (!isHost || finalizing) return;
    
    setFinalizing(true);
    try {
      await finalizeVotes(lobby.code);
    } catch (error) {
      console.error('Error finalizing votes:', error);
      setFinalizing(false);
    }
  };
  
  const voteCounts = Object.values(votes).reduce((acc, targetId) => {
    acc[targetId] = (acc[targetId] || 0) + 1;
    return acc;
  }, {});
  
  // Eliminated players see a spectator view
  if (isEliminated) {
    return (
      <div className="voting-phase">
        <div className="voting-header">
          <h2>Oylama - Tur {roundNumber}</h2>
          <div className="voting-status">
            <span className="voting-status-label">Oylar:</span>
            <span className="voting-status-count">{votedPlayerCount}/{totalPlayerCount}</span>
          </div>
        </div>
        <div className="voting-eliminated-banner">
          <p>Elendin! Oylamayı izliyorsun.</p>
        </div>
        <div className="voting-results-preview">
          <h3>Mevcut Oy Durumu</h3>
          <div className="voting-player-results">
            {alivePlayers.map(player => {
              const voteCount = voteCounts[player.id] || 0;
              return (
                <div key={player.id} className="voting-player-result">
                  <div className="voting-player-result-name">{player.name}</div>
                  <div className="voting-player-result-bar-container">
                    <div className="voting-player-result-bar" style={{ width: `${(voteCount / totalPlayerCount) * 100}%` }}></div>
                    <span className="voting-player-result-count">{voteCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="voting-footer">
          <p className="voting-message">Oylama tamamlandığında sonuçlar gösterilecek.</p>
          {isHost && votedPlayerCount === totalPlayerCount && (
            <Button onClick={handleFinalizeVotes} variant="primary" fullWidth={true} disabled={finalizing}>
              {finalizing ? 'İşleniyor...' : 'Sonuçları Göster'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // If user has already voted or voting is complete
  if (hasVoted || voted) {
    return (
      <div className="voting-phase">
        <div className="voting-header">
          <h2>Oylama - Tur {roundNumber}</h2>
          <div className="voting-status">
            <span className="voting-status-label">Oylar:</span>
            <span className="voting-status-count">{votedPlayerCount}/{totalPlayerCount}</span>
          </div>
        </div>
        
        <div className="voting-results-preview">
          <h3>Mevcut Oy Durumu</h3>
          <div className="voting-player-results">
            {alivePlayers.map(player => {
              const voteCount = voteCounts[player.id] || 0;
              return (
                <div key={player.id} className="voting-player-result">
                  <div className="voting-player-result-name">
                    {player.name}
                  </div>
                  <div className="voting-player-result-bar-container">
                    <div 
                      className="voting-player-result-bar"
                      style={{ width: `${(voteCount / totalPlayerCount) * 100}%` }}
                    ></div>
                    <span className="voting-player-result-count">{voteCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="voting-footer">
          <p className="voting-message">
            {isHost ? 
              'Tüm oyuncular oy verdiğinde, oylama sonuçlarını sonlandırabilirsiniz.' :
              'Oylama tamamlandığında sonuçlar gösterilecek.'
            }
          </p>
          
          {isHost && votedPlayerCount === totalPlayerCount && (
            <Button 
              onClick={handleFinalizeVotes}
              variant="primary"
              fullWidth={true}
              disabled={finalizing}
            >
              {finalizing ? 'İşleniyor...' : 'Sonuçları Göster'}
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  // Active voting UI
  return (
    <div className="voting-phase">
      <div className="voting-header">
        <h2>Köy Oylaması - Tur {roundNumber}</h2>
        <p className="voting-description">
          Kim köyden atılsın? Kurt takımı olduğunu düşündüğün bir oyuncu seç.
        </p>
        <div className="voting-status">
          <span className="voting-status-label">Oylar:</span>
          <span className="voting-status-count">{votedPlayerCount}/{totalPlayerCount}</span>
        </div>
      </div>
      
      <div className="voting-players">
        {alivePlayers.filter(player => player.id !== playerId).map(player => (
          <div
            key={player.id}
            className={`voting-player ${selectedPlayerId === player.id ? 'voting-player--selected' : ''}`}
            onClick={() => setSelectedPlayerId(player.id)}
          >
            <div className="voting-player-avatar">
              {player.name.charAt(0)}
            </div>
            <div className="voting-player-name">
              {player.name}
            </div>
            {selectedPlayerId === player.id && (
              <div className="voting-player-check">✓</div>
            )}
          </div>
        ))}
      </div>
      
      <div className="voting-instruction">
        <p>Oyunu vermek için bir oyuncu seçin.</p>
      </div>
      
      <div className="voting-footer">
        <Button
          onClick={handleVote}
          variant="primary"
          disabled={!selectedPlayerId || isSubmitting}
          fullWidth={true}
        >
          {isSubmitting ? 'Oy Veriliyor...' : 'Oy Ver'}
        </Button>
      </div>
    </div>
  );
};

export default VotingPhase;
